import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";

import {
  Typography,
  Row,
  Col,
  DatePicker,
  Form,
  Input,
  Button,
  Result,
  Select,
  Slider,
  Spin,
  Table,
  AutoComplete,
} from "antd";

import {
  apiLookupSearch,
  apiLookupPurchase,
  selectApiSearchResult,
  selectApiSearchLoading,
  selectApiSearchError,
  selectApiPurchaseData,
  selectApiPurchaseLoading,
  selectApiPurchaseError,
  apiAutocompleteSearch,
  apiAutocompleteSearchForContractor,
  selectAutoCompleteResult,
  clear,
  selectAutoCompleteResultForContractor,
  selectAutoCompleteResultForArchitect,
  apiAutocompleteSearchForArchitect,
  selectAutoCompleteResultForEngineer,
  apiAutocompleteSearchForEngineer,
  selectAutoCompleteResultForDesigner,
  apiAutocompleteSearchForDesigner,
} from "./apiLookupSlice";

import { selectInfo } from "../../features/profile/profileSlice";

import { Layout } from "@builty/components/Layout";

import { formatCurrency } from "../../utils";
import {
  CATEGORIES,
  STATUSES,
  MAX_ROWS,
  CA_COUNTIES,
  CA_MSAS,
} from "../../constants";

import "./ApiLookup.scss";

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const MAX_JOB_VALUE = 1000000;

const COLUMNS = [
  {
    dataIndex: "jurisdiction",
    title: "Jurisdiction",
    width: 120,
  },
  {
    dataIndex: "record_number",
    title: "Record Number",
    width: 150,
  },
  {
    dataIndex: "record_type_original",
    title: "Record Type",
    width: 150,
  },
  {
    dataIndex: "status",
    title: "Status",
    width: 100,
  },
  {
    dataIndex: "file_date",
    title: "File Date",
    width: 120,
  },
  {
    dataIndex: "permit_date",
    title: "Permit Date",
    width: 120,
  },
  {
    dataIndex: "final_date",
    title: "Final Date",
    width: 120,
  },
  {
    dataIndex: "apn",
    title: "Parcel Number",
    width: 130,
  },
  {
    dataIndex: "street",
    title: "Street",
    width: 150,
  },
  {
    dataIndex: "city",
    title: "City",
    width: 120,
  },
  {
    dataIndex: "state",
    title: "State",
    width: 50,
  },
  {
    dataIndex: "zipcode",
    title: "Zip Code",
    width: 100,
  },
  {
    dataIndex: "description",
    title: "Description",
    width: 400,
    render: (desc) => (
      <span title={desc} className="c-api-lookup-cell-desc">
        {desc}
      </span>
    ),
  },
  {
    dataIndex: "job_value",
    title: "Job Value",
    render: (amount) => (amount ? `$${formatCurrency(amount)}` : null),
    width: 100,
  },
  {
    dataIndex: "total_fees",
    title: "Total Fees",
    render: (amount) => (amount ? `$${formatCurrency(amount)}` : null),
    width: 100,
  },
  {
    dataIndex: "contractor_license",
    title: "Contractor License",
    width: 150,
  },
  {
    dataIndex: "contractor_name",
    title: "Contractor Name",
    width: 150,
  },
  {
    dataIndex: "contractor_biz_name",
    title: "Contractor Business Name",
    width: 200,
  },
  {
    dataIndex: "contractor_street",
    title: "Contractor Street",
    width: 150,
  },
  {
    dataIndex: "contractor_city",
    title: "Contractor City",
    width: 150,
  },
  {
    dataIndex: "contractor_state",
    title: "Contractor State",
    width: 150,
  },
  {
    dataIndex: "contractor_zipcode",
    title: "Contractor Zip Code",
    width: 180,
  },
  {
    dataIndex: "contractor_phone",
    title: "Contractor Phone",
    width: 150,
  },
  {
    dataIndex: "contractor_email",
    title: "Contractor Email",
    width: 150,
  },
  {
    dataIndex: "architect_license",
    title: "Architect License",
    width: 150,
  },
  {
    dataIndex: "architect_name",
    title: "Architect Name",
    width: 150,
  },
  {
    dataIndex: "architect_biz_name",
    title: "Architect Business Name",
    width: 200,
  },
  {
    dataIndex: "architect_street",
    title: "Architect Street",
    width: 150,
  },
  {
    dataIndex: "architect_city",
    title: "Architect City",
    width: 150,
  },
  {
    dataIndex: "architect_state",
    title: "Architect State",
    width: 150,
  },
  {
    dataIndex: "architect_zipcode",
    title: "Architect Zip Code",
    width: 180,
  },
  {
    dataIndex: "architect_phone",
    title: "Architect Phone",
    width: 150,
  },
  {
    dataIndex: "architect_email",
    title: "Architect Email",
    width: 150,
  },
  {
    dataIndex: "designer_license",
    title: "Designer License",
    width: 150,
  },
  {
    dataIndex: "designer_name",
    title: "Designer Name",
    width: 150,
  },
  {
    dataIndex: "designer_biz_name",
    title: "Designer Business Name",
    width: 200,
  },
  {
    dataIndex: "designer_street",
    title: "Designer Street",
    width: 150,
  },
  {
    dataIndex: "designer_city",
    title: "Designer City",
    width: 150,
  },
  {
    dataIndex: "designer_state",
    title: "Designer State",
    width: 150,
  },
  {
    dataIndex: "designer_zipcode",
    title: "Designer Zip Code",
    width: 180,
  },
  {
    dataIndex: "designer_phone",
    title: "Designer Phone",
    width: 150,
  },
  {
    dataIndex: "designer_email",
    title: "Designer Email",
    width: 150,
  },
  {
    dataIndex: "engineer_license",
    title: "Engineer License",
    width: 150,
  },
  {
    dataIndex: "engineer_name",
    title: "Engineer Name",
    width: 150,
  },
  {
    dataIndex: "engineer_biz_name",
    title: "Engineer Business Name",
    width: 200,
  },
  {
    dataIndex: "engineer_street",
    title: "Engineer Street",
    width: 150,
  },
  {
    dataIndex: "engineer_city",
    title: "Engineer City",
    width: 150,
  },
  {
    dataIndex: "engineer_state",
    title: "Engineer State",
    width: 150,
  },
  {
    dataIndex: "engineer_zipcode",
    title: "Engineer Zip Code",
    width: 150,
  },
  {
    dataIndex: "engineer_phone",
    title: "Engineer Phone",
    width: 150,
  },
  {
    dataIndex: "engineer_email",
    title: "Engineer Email",
    width: 150,
  },
];

export const ApiLookup = ({ isLoggedIn, onLogin }) => {
  const [form] = Form.useForm();

  const dispatch = useDispatch();

  const [loginCallback, setLoginCallback] = useState([null]);

  const searchResult = useSelector(selectApiSearchResult);
  const searchLoading = useSelector(selectApiSearchLoading);
  const searchError = useSelector(selectApiSearchError);
  const data = useSelector(selectApiPurchaseData);
  const purchaseLoading = useSelector(selectApiPurchaseLoading);
  const purchaseError = useSelector(selectApiPurchaseError);
  const { freeAddressLookups, paymentMethods, apiTokens } =
    useSelector(selectInfo);
  const autoComplete = useSelector(selectAutoCompleteResult);
  const [options, setOptions] = useState([]);
  const [key, setKey] = useState("");
  const autoCompleteRef = useRef();
  const autoCompleteForContractor = useSelector(
    selectAutoCompleteResultForContractor
  );
  const [optionsForContractor, setOptionsForContractor] = useState([]);
  const [contractorKey, setContractorKey] = useState("");

  const autoCompleteForArchitect = useSelector(
    selectAutoCompleteResultForArchitect
  );
  const [optionsForArchitect, setOptionsForArchitect] = useState([]);
  const [architectKey, setArchitectKey] = useState("");

  const autoCompleteForEngineer = useSelector(
    selectAutoCompleteResultForEngineer
  );
  const [optionsForEngineer, setOptionsForEngineer] = useState([]);
  const [engineerKey, setEngineerKey] = useState("");

  const autoCompleteForDesigner = useSelector(
    selectAutoCompleteResultForDesigner
  );
  const [optionsForDesigner, setOptionsForDesigner] = useState([]);
  const [designerKey, setDesignerKey] = useState("");

  useEffect(() => {
    if (apiTokens && loginCallback[0]) {
      loginCallback[0]();
      setLoginCallback([null]);
    }
  }, [apiTokens, loginCallback]);

  useEffect(() => {
    setOptions([]);

    if (autoComplete && autoComplete.suggestions) {
      console.log(autoComplete);
      const suggestions = autoComplete.suggestions;
      const filter = suggestions.filter((value) => value.includes(key));
      const filter1 = [...new Set(autoComplete.suggestions)];
      console.log({filter1});
      setOptions(filter1);
    }
  }, [autoComplete]);

  useEffect(() => {
    dispatch(apiAutocompleteSearch(key));
  }, [key]);

  useEffect(() => {
    setOptionsForContractor([]);
    if (autoCompleteForContractor && autoCompleteForContractor.suggestions) {
      // console.log({ autoCompleteForContractor });
      const filter2 = [...new Set(autoCompleteForContractor.suggestions)];
      // console.log({ filter2 });
      setOptionsForContractor(filter2);
    }
  }, [autoCompleteForContractor]);

  useEffect(() => {
    dispatch(apiAutocompleteSearchForContractor(contractorKey));
  }, [contractorKey]);

  useEffect(() => {
    setOptionsForArchitect([]);
    if (autoCompleteForArchitect && autoCompleteForArchitect.suggestions) {
      // console.log({ autoCompleteForArchitect });
      const filter3 = [...new Set(autoCompleteForArchitect.suggestions)];
      // console.log({ filter3 });
      setOptionsForArchitect(filter3);
    }
  }, [autoCompleteForArchitect]);

  useEffect(() => {
    dispatch(apiAutocompleteSearchForArchitect(architectKey));
  }, [architectKey]);

  useEffect(() => {
    setOptionsForEngineer([]);
    if (autoCompleteForEngineer && autoCompleteForEngineer.suggestions) {
      // console.log({ autoCompleteForEngineer });
      const filter4 = [...new Set(autoCompleteForEngineer.suggestions)];
      // console.log({ filter4 });
      setOptionsForEngineer(filter4);
    }
  }, [autoCompleteForEngineer]);

  useEffect(() => {
    dispatch(apiAutocompleteSearchForEngineer(engineerKey));
  }, [engineerKey]);

  useEffect(() => {
    setOptionsForDesigner([]);
    if (autoCompleteForDesigner && autoCompleteForDesigner.suggestions) {
      // console.log({ autoCompleteForDesigner });
      const filter5 = [...new Set(autoCompleteForDesigner.suggestions)];
      // console.log({ filter5 });
      setOptionsForDesigner(filter5);
    }
  }, [autoCompleteForDesigner]);

  useEffect(() => {
    dispatch(apiAutocompleteSearchForDesigner(designerKey));
  }, [designerKey]);

  const doSearch = (values) => {
    const rangeNames = ["file_date", "permit_date", "final_date"];
    rangeNames.forEach((name) => {
      const orig = values[name] ?? [null, null];
      values[name] = [
        orig[0]?.format("YYYY-MM-DD"),
        orig[1]?.format("YYYY-MM-DD"),
      ];
    });
    if (values["job_value"][1] > MAX_JOB_VALUE) {
      values["job_value"][1] = null;
    }
    console.log(values['counties'])
    if(values['counties']) {
      const dataArray = values['counties'].split(',');
      const trimmedCounties = dataArray.map(county => county.trim());
      values['counties'] = trimmedCounties
    } else {
      values['counties'] = []
    }
    dispatch(apiLookupSearch(values));
  };

  const onFormSubmit = (values) => {
    if (isLoggedIn) {
      doSearch(values);
    } else {
      onLogin();
      setLoginCallback([
        () => {
          doSearch(values);
        },
      ]);
    }
  };

  const onReset = () => {
    dispatch(clear());
    form.resetFields();
  };

  const onPurchase = () => {
    dispatch(apiLookupPurchase());
  };

  const formatTooltip = (number) =>
    number > MAX_JOB_VALUE ? "$∞" : `$${formatCurrency(number, 0)}`;

  const isFormDisabled = searchLoading;

  return (
    <Layout.Content>
      <Form
        form={form}
        initialValues={{
          state: "CA",
          status: "",
          job_value: [0, 1000001],
        }}
        name="api-lookup-form"
        layout="horizontal"
        size="large"
        onFinish={onFormSubmit}
        className="c-api-lookup-form"
      >
        <Row gutter={16}>
          <Col span={8}>
            <Title level={4}>Location</Title>
          </Col>
          <Col span={8}>
            <Title level={4}>Permit Features</Title>
          </Col>
          <Col span={8}>
            <Title level={4}>Contacts</Title>
          </Col>
        </Row>
        {console.log(
          window.innerWidth,
          window.innerWidth > 1495 && window.innerWidth < 1562
        )}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              className="after"
              colon={
                window.innerWidth > 1000 && window.innerWidth < 1108
                  ? true
                  : false
              }
              labelCol={{
                xs: { span: 12 },
                sm: { span: 12 },
                md: { span: 9 },
                lg: { span: 10 },
                xl: { span: 7 },
              }}
              labelAlign={"left"}
              name="msas"
              label={
                <span className="ant-form-item-label1">
                  {window.innerWidth > 1000 && window.innerWidth < 1108
                    ? "Metropolitan Statistical Area"
                    : "Metropolitan Statistical Area :"}
                </span>
              }
            >
              <Select mode="multiple" allowClear disabled={isFormDisabled}>
                {CA_MSAS.map((value) => (
                  <Option key={value} value={value}>
                    {value}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              labelCol={{
                xs: { span: 12 },
                sm: { span: 12 },
                md: { span: 9 },
                lg: { span: 10 },
                xl: { span: 7 },
              }}
              labelAlign={"left"}
              name="category"
              label="Category"
            >
              <Select mode="multiple" allowClear disabled={isFormDisabled}>
                {CATEGORIES.map((value) => (
                  <Option key={value} value={value}>
                    {value}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              labelCol={{
                xs: { span: 12 },
                sm: { span: 12 },
                md: { span: 9 },
                lg: { span: 10 },
                xl: { span: 7 },
              }}
              labelAlign={"left"}
              name="contractor"
              label="Contractor"
            >
              {/* <Input disabled={isFormDisabled} /> */}
              <AutoComplete
                disabled={isFormDisabled}
                showSearch
                onSearch={(value) => {
                  console.log(value, value.length, value.length > 3);

                  if (value.length > 3) {
                    setOptionsForContractor([]);
                    setContractorKey(value);
                  }
                  if (value.length <= 1) {
                    setOptionsForContractor([]);
                  }
                }}
                onClear={() => {
                  setOptionsForContractor([]);
                }}
                // key={autoComplete}
                // aria-autocomplete='list'
                defaultActiveFirstOption={false}
                allowClear
                // ref={autoCompleteRef}
              >
                {autoCompleteForContractor &&
                  optionsForContractor.map((value) => (
                    <AutoComplete.Option key={value} value={value}>
                      {value}
                    </AutoComplete.Option>
                  ))}
              </AutoComplete>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              labelCol={{
                xs: { span: 12 },
                sm: { span: 12 },
                md: { span: 9 },
                lg: { span: 10 },
                xl: { span: 7 },
              }}
              labelAlign={"left"}
              name="state"
              label="State"
            >
              <Select disabled={isFormDisabled}>
                <Option value="CA">California</Option>
                <Option value="FL">Florida</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              labelCol={{
                xs: { span: 12 },
                sm: { span: 12 },
                md: { span: 9 },
                lg: { span: 10 },
                xl: { span: 7 },
              }}
              labelAlign={"left"}
              name="status"
              label="Status Group"
            >
              <Select disabled={isFormDisabled}>
                <Option value="">(Any)</Option>
                {STATUSES.map((value) => (
                  <Option key={value} value={value}>
                    {value}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              labelCol={{
                xs: { span: 12 },
                sm: { span: 12 },
                md: { span: 9 },
                lg: { span: 10 },
                xl: { span: 7 },
              }}
              labelAlign={"left"}
              name="architect"
              label="Architect"
            >
              {/* <Input disabled={isFormDisabled} /> */}
              <AutoComplete
                disabled={isFormDisabled}
                showSearch
                onSearch={(value) => {
                  console.log(value, value.length, value.length > 3);

                  if (value.length > 3) {
                    setOptionsForArchitect([]);
                    setArchitectKey(value);
                  }
                  if (value.length <= 1) {
                    setOptionsForArchitect([]);
                  }
                }}
                onClear={() => {
                  setOptionsForArchitect([]);
                }}
                // key={autoComplete}
                // aria-autocomplete='list'
                defaultActiveFirstOption={false}
                allowClear
                // ref={autoCompleteRef}
              >
                {autoCompleteForArchitect &&
                  optionsForArchitect.map((value) => (
                    <AutoComplete.Option key={value} value={value}>
                      {value}
                    </AutoComplete.Option>
                  ))}
              </AutoComplete>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              labelCol={{
                xs: { span: 12 },
                sm: { span: 12 },
                md: { span: 9 },
                lg: { span: 10 },
                xl: { span: 7 },
              }}
              labelAlign={"left"}
              name="counties"
              label="County"
            >
              <Input
                disabled={isFormDisabled}
                placeholder="Los Angeles County, Alpine County"
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              labelCol={{
                xs: { span: 12 },
                sm: { span: 12 },
                md: { span: 9 },
                lg: { span: 10 },
                xl: { span: 7 },
              }}
              labelAlign={"left"}
              name="file_date"
              label="File Date"
            >
              <RangePicker
                allowEmpty={[true, true]}
                disabled={isFormDisabled}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              labelCol={{
                xs: { span: 12 },
                sm: { span: 12 },
                md: { span: 9 },
                lg: { span: 10 },
                xl: { span: 7 },
              }}
              labelAlign={"left"}
              name="designer"
              label="Designer"
            >
              {/* <Input disabled={isFormDisabled} /> */}
              <AutoComplete
                disabled={isFormDisabled}
                showSearch
                onSearch={(value) => {
                  console.log(value, value.length, value.length > 3);

                  if (value.length > 3) {
                    setOptionsForDesigner([]);
                    setDesignerKey(value);
                  }
                  if (value.length <= 1) {
                    setOptionsForDesigner([]);
                  }
                }}
                onClear={() => {
                  setOptionsForDesigner([]);
                }}
                // key={autoComplete}
                // aria-autocomplete='list'
                defaultActiveFirstOption={false}
                allowClear
                // ref={autoCompleteRef}
              >
                {autoCompleteForDesigner &&
                  optionsForDesigner.map((value) => (
                    <AutoComplete.Option key={value} value={value}>
                      {value}
                    </AutoComplete.Option>
                  ))}
              </AutoComplete>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              labelCol={{
                xs: { span: 12 },
                sm: { span: 12 },
                md: { span: 9 },
                lg: { span: 10 },
                xl: { span: 7 },
              }}
              labelAlign={"left"}
              name="city"
              label="City"
            >
              <Input disabled={isFormDisabled} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              labelCol={{
                xs: { span: 12 },
                sm: { span: 12 },
                md: { span: 9 },
                lg: { span: 10 },
                xl: { span: 7 },
              }}
              labelAlign={"left"}
              name="permit_date"
              label="Permit Date"
            >
              <RangePicker
                allowEmpty={[true, true]}
                disabled={isFormDisabled}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              labelCol={{
                xs: { span: 12 },
                sm: { span: 12 },
                md: { span: 9 },
                lg: { span: 10 },
                xl: { span: 7 },
              }}
              labelAlign={"left"}
              name="engineer"
              label="Engineer"
            >
              {/* <Input disabled={isFormDisabled} /> */}
              <AutoComplete
                disabled={isFormDisabled}
                showSearch
                onSearch={(value) => {
                  console.log(value, value.length, value.length > 3);

                  if (value.length > 3) {
                    setOptionsForEngineer([]);
                    setEngineerKey(value);
                  }
                  if (value.length <= 1) {
                    setOptionsForEngineer([]);
                  }
                }}
                onClear={() => {
                  setOptionsForEngineer([]);
                }}
                // key={autoComplete}
                // aria-autocomplete='list'
                defaultActiveFirstOption={false}
                allowClear
                // ref={autoCompleteRef}
              >
                {autoCompleteForEngineer &&
                  optionsForEngineer.map((value) => (
                    <AutoComplete.Option key={value} value={value}>
                      {value}
                    </AutoComplete.Option>
                  ))}
              </AutoComplete>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              labelCol={{
                xs: { span: 12 },
                sm: { span: 12 },
                md: { span: 9 },
                lg: { span: 10 },
                xl: { span: 7 },
              }}
              labelAlign={"left"}
              name="zipcode"
              label="Zip Code"
            >
              <Input disabled={isFormDisabled} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              labelCol={{
                xs: { span: 12 },
                sm: { span: 12 },
                md: { span: 9 },
                lg: { span: 10 },
                xl: { span: 7 },
              }}
              labelAlign={"left"}
              name="final_date"
              label="Final Date"
            >
              <RangePicker
                allowEmpty={[true, true]}
                disabled={isFormDisabled}
              />
            </Form.Item>
          </Col>
          <Col span={8}></Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              labelCol={{
                xs: { span: 12 },
                sm: { span: 12 },
                md: { span: 9 },
                lg: { span: 10 },
                xl: { span: 7 },
              }}
              labelAlign={"left"}
              name="street"
              label="Address"
            >
              <AutoComplete
                disabled={isFormDisabled}
                showSearch
                onSearch={(value) => {
                  console.log(value, value.length, value.length > 3);

                  if (value.length > 3) {
                    setOptions([]);
                    setKey(value);
                  }
                  if (value.length <= 1) {
                    setOptions([]);
                  }
                }}
                onClear={() => {
                  setOptions([]);
                }}
                // key={autoComplete}
                // aria-autocomplete='list'
                defaultActiveFirstOption={false}
                allowClear
                // ref={autoCompleteRef}
              >
                {autoComplete &&
                  options.map((value) => (
                    <AutoComplete.Option key={value} value={value}>
                      {value}
                    </AutoComplete.Option>
                  ))}
              </AutoComplete>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              labelCol={{
                xs: { span: 12 },
                sm: { span: 12 },
                md: { span: 9 },
                lg: { span: 10 },
                xl: { span: 7 },
              }}
              labelAlign={"left"}
              name="job_value"
              label="Job Value"
            >
              <Slider
                range
                min={0}
                max={MAX_JOB_VALUE + 1}
                step={1000}
                disabled={isFormDisabled}
                tooltip={{ formatter: formatTooltip }}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              labelCol={{
                xs: { span: 12 },
                sm: { span: 12 },
                md: { span: 9 },
                lg: { span: 10 },
                xl: { span: 7 },
              }}
              labelAlign={"left"}
            >
              <div style={{ display: "flex", flexFlow: "row nowrap" }}>
                <div style={{ flexGrow: "1", marginRight: "16px" }}>
                  <Button
                    htmlType="button"
                    block
                    onClick={onReset}
                    disabled={isFormDisabled}
                  >
                    Reset
                  </Button>
                </div>
                <div style={{ flexGrow: "3" }}>
                  <Button
                    type="primary"
                    block
                    htmlType="submit"
                    disabled={isFormDisabled && !searchLoading}
                    loading={searchLoading}
                  >
                    Lookup
                  </Button>
                </div>
              </div>
            </Form.Item>
          </Col>
        </Row>

        {searchError && (
          <Row gutter={16} align="center">
            {searchError}{" "}
          </Row>
        )}

        {searchResult !== null && !searchLoading && (
          <Result
            status={
              searchResult.count && searchResult.count <= MAX_ROWS
                ? "success"
                : "info"
            }
            title={
              searchResult.count ? (
                searchResult.count <= MAX_ROWS ? (
                  <>Found {searchResult.count} record(s).</>
                ) : (
                  <>
                    More than {MAX_ROWS.toLocaleString()} results &ndash; add
                    more filters
                  </>
                )
              ) : (
                <>No records found</>
              )
            }
            extra={
              <div>
                <div>
                  <Button
                    type="primary"
                    loading={purchaseLoading}
                    disabled={
                      !searchResult.count ||
                      searchResult.count > MAX_ROWS ||
                      data?.url
                    }
                    onClick={onPurchase}
                  >
                    Purchase
                  </Button>
                </div>
                <div>
                  {purchaseLoading ? <Spin size="small" /> : freeAddressLookups}{" "}
                  free permits left
                </div>
                {purchaseError && <div>{purchaseError} </div>}
                {data?.url && (
                  <div>
                    {
                      <Button type="primary" href={data.url}>
                        Download
                      </Button>
                    }{" "}
                  </div>
                )}
              </div>
            }
          />
        )}
      </Form>

      {searchResult && (
        <Table
          columns={COLUMNS}
          dataSource={searchResult.preview}
          rowKey={(record) => `${record.jurisdiction}-${record.record_number}`}
          pagination={{ hideOnSinglePage: true }}
          scroll={{ x: "max-content" }}
        />
      )}
    </Layout.Content>
  );
};
